import { tutorialAssets } from "assets/cdn";
import { useImagePath } from "hooks/useImagePath";
import { useTutorialAnchor } from "../../hooks";
import { TutorialV2StepProps } from "../../types";
import { getSwiperSlideSpeed } from "utils/getSwiperSlideSpeed";
import { CrownIcon } from "utils/svgIcons";
import { FC, useEffect, useState } from "react";
import type { Swiper as SwiperClass } from "swiper/types";
import {
	Arrow,
	ArrowDownContainer,
	Container,
	Content,
	NextButton,
	SkipButton,
	Slide,
	SlideNumber,
	Subtitle,
	Tiki,
	Title
} from "../styles";
import {
	MenuButtonHighlight,
	MenuButtonShine,
	SelectedMenuButton
} from "./styles";

const LOYALTY_BUTTON_ID = "page-top-nav-main-menu-crown";
const SWIPER_MARGIN = 10;
const SLIDE_WIDTH_EM = 33.1;
const ARROW_CENTER_EM = 23.8;

type SwiperElement = HTMLElement & { swiper?: SwiperClass };

export const Step2: FC<TutorialV2StepProps> = ({
	currentStep,
	totalSteps,
	onNext,
	onSkip
}) => {
	const { getImage } = useImagePath("cdn");
	const background = getImage(tutorialAssets.background);
	const ArrowImage = getImage(tutorialAssets.greenArrow);
	const ShineImage = getImage(tutorialAssets.shine);
	const TikiImage = getImage(tutorialAssets.char2);
	const [isButtonReady, setIsButtonReady] = useState(false);
	const [buttonText, setButtonText] = useState("Loyalty Shop");
	const { scheduleMeasure, targetRect } = useTutorialAnchor({
		targetId: LOYALTY_BUTTON_ID,
		shouldScroll: false
	});

	useEffect(() => {
		let button: HTMLElement | null = null;
		let observer: MutationObserver | null = null;
		let repositionTimeout: number | null = null;
		let settleTimeout: number | null = null;
		let wasActive = false;
		const viewport = window.visualViewport;

		const finishPositioning = (delay: number) => {
			if (settleTimeout !== null) {
				window.clearTimeout(settleTimeout);
			}
			setIsButtonReady(false);
			settleTimeout = window.setTimeout(() => {
				scheduleMeasure();
				setIsButtonReady(true);
				settleTimeout = null;
			}, delay + 50);
		};

		const positionButton = (targetButton: HTMLElement) => {
			const swiperElement = targetButton.closest<SwiperElement>(".swiper");
			const slide = targetButton.closest<HTMLElement>(".swiper-slide");
			const swiper = swiperElement?.swiper;
			const swiperRect = swiperElement?.getBoundingClientRect();

			if (!swiper || !swiperRect || !slide) {
				finishPositioning(0);
				return;
			}

			const buttonRect = targetButton.getBoundingClientRect();
			const tutorialRoot = document.querySelector<HTMLElement>(
				"[data-tutorial-v2-root]"
			);
			const oneEm = tutorialRoot
				? parseFloat(window.getComputedStyle(tutorialRoot).fontSize)
				: 10;
			const viewportLeft = viewport?.offsetLeft ?? 0;
			const viewportWidth = viewport?.width ?? window.innerWidth;
			const buttonCenter = buttonRect.left + buttonRect.width / 2;
			const slideLeft = buttonCenter - ARROW_CENTER_EM * oneEm;
			const slideRight = slideLeft + SLIDE_WIDTH_EM * oneEm;
			const isButtonVisible =
				buttonRect.left >= swiperRect.left + SWIPER_MARGIN &&
				buttonRect.right <= swiperRect.right - SWIPER_MARGIN;
			const isSlideVisible =
				slideLeft >= viewportLeft + oneEm &&
				slideRight <= viewportLeft + viewportWidth - oneEm;

			if (isButtonVisible && isSlideVisible) {
				finishPositioning(0);
				return;
			}

			const centeredButtonPosition =
				viewportLeft +
				viewportWidth / 2 +
				(ARROW_CENTER_EM - SLIDE_WIDTH_EM / 2) * oneEm;
			const minimumButtonCenter =
				swiperRect.left + SWIPER_MARGIN + buttonRect.width / 2;
			const maximumButtonCenter =
				swiperRect.right - SWIPER_MARGIN - buttonRect.width / 2;
			const desiredButtonCenter = Math.min(
				maximumButtonCenter,
				Math.max(minimumButtonCenter, centeredButtonPosition)
			);
			const nextTranslate =
				swiper.translate + desiredButtonCenter - buttonCenter;
			const slideIndex = Array.from(swiper.slides).indexOf(slide);
			const speed = getSwiperSlideSpeed(slideIndex - swiper.activeIndex);

			finishPositioning(speed);
			swiper.translateTo(nextTranslate, speed, false, true);
		};

		const connectButton = () => {
			const nextButton = document.getElementById(LOYALTY_BUTTON_ID);
			if (!nextButton || nextButton === button) {
				return !!nextButton;
			}

			button = nextButton;
			wasActive = nextButton.classList.contains("is-active");
			nextButton.classList.add("is-active");
			setButtonText(nextButton.textContent?.trim() || "Loyalty Shop");

			if (window.scrollY > 0) {
				window.scrollTo({ top: 0, behavior: "auto" });
			}

			positionButton(nextButton);

			return true;
		};

		if (!connectButton()) {
			observer = new MutationObserver(() => {
				if (connectButton()) {
					observer?.disconnect();
					observer = null;
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });
		}

		const handleViewportChange = () => {
			setIsButtonReady(false);
			if (repositionTimeout !== null) {
				window.clearTimeout(repositionTimeout);
			}
			repositionTimeout = window.setTimeout(() => {
				if (button) {
					positionButton(button);
				}
				repositionTimeout = null;
			}, 250);
		};

		window.addEventListener("resize", handleViewportChange);
		window.addEventListener("orientationchange", handleViewportChange);
		viewport?.addEventListener("resize", handleViewportChange);

		return () => {
			observer?.disconnect();
			window.removeEventListener("resize", handleViewportChange);
			window.removeEventListener("orientationchange", handleViewportChange);
			viewport?.removeEventListener("resize", handleViewportChange);
			if (repositionTimeout !== null) {
				window.clearTimeout(repositionTimeout);
			}
			if (settleTimeout !== null) {
				window.clearTimeout(settleTimeout);
			}
			if (button && !wasActive) {
				button.classList.remove("is-active");
			}
		};
	}, [scheduleMeasure]);

	const buttonRect = isButtonReady ? targetRect : null;

	return (
		<>
			{buttonRect && (
				<MenuButtonHighlight
					$height={buttonRect.height}
					$left={buttonRect.left}
					$top={buttonRect.top}
					$width={buttonRect.width}
				>
					<MenuButtonShine src={ShineImage} />
					<SelectedMenuButton className="default-nav is-active">
						<CrownIcon />
						<span>{buttonText}</span>
					</SelectedMenuButton>
				</MenuButtonHighlight>
			)}
			<Container
				$anchorCenterOffset={buttonRect ? buttonRect.width / 2 : 0}
				$anchorLeft={buttonRect?.left}
				$anchorOffset={5.5}
				$anchorTop={buttonRect?.bottom}
				$arrowCenter={ARROW_CENTER_EM}
				$placement="below"
			>
				<ArrowDownContainer>
					<Arrow src={ArrowImage} />
				</ArrowDownContainer>
				<SlideNumber>
					{currentStep}/{totalSteps}
				</SlideNumber>
				<Slide $backgroundImage={background}>
					<Tiki src={TikiImage} />
					<Content>
						<Title>
							You also earn Loyalty Points
							<br /> on every purchase.
						</Title>
						<Subtitle>Spend them later for free rewards.</Subtitle>
					</Content>
					<NextButton onClick={onNext}>Next</NextButton>
					<SkipButton onClick={onSkip}>Skip</SkipButton>
				</Slide>
			</Container>
		</>
	);
};
